import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TabbedCard } from '../../../../components/ui/tabbedCard/TabbedCard';
import { toBulletinMethods, toAcompteMethods, toSoldeMethods } from '../../utils/paymentMethods';
import type { PaymentMethod, PaymentTabKey } from '../../utils/paymentMethods';
import type { EmployeeRow } from '../../employeeTypes';
import { SECTION_ICON, SECTION_VISUAL } from '../../config/employeeDetailTokens';

type PaymentMethodsCardProps = {
  employee: EmployeeRow;
  stretch?: boolean;
  collapsible?: boolean;
  defaultExpanded?: boolean;
};

/**
 * Employee-specific payment methods card.
 * Owns all configuration (tabs, columns, translations, visual tokens)
 * and delegates rendering to the generic TabbedCard component.
 */
export function PaymentMethodsCard({
  employee,
  stretch,
  collapsible,
  defaultExpanded,
}: PaymentMethodsCardProps) {
  const { t } = useTranslation('common');

  const tabData = useMemo(
    () => ({
      bulletins: toBulletinMethods(employee),
      acomptes: toAcompteMethods(employee),
      soldes: toSoldeMethods(employee),
    }),
    [employee],
  );

  return (
    <TabbedCard<PaymentTabKey, PaymentMethod>
      title={t('employeeDetail.sections.payment')}
      icon={SECTION_ICON.payment(SECTION_VISUAL.payment.iconColor)}
      iconBg={SECTION_VISUAL.payment.iconBg}
      tabs={[
        { key: 'bulletins', label: t('employeeDetail.payment.tabBulletins') },
        { key: 'acomptes', label: t('employeeDetail.payment.tabAcomptes') },
        { key: 'soldes', label: t('employeeDetail.payment.tabSoldes') },
      ]}
      columns={[
        {
          key: 'mode',
          label: t('employeeDetail.payment.colMode'),
          getValue: (m: PaymentMethod) => m.mode,
        },
        {
          key: 'operator',
          label: t('employeeDetail.payment.colBank'),
          getValue: (m: PaymentMethod) => m.operator,
        },
        {
          key: 'accountNumber',
          label: t('employeeDetail.payment.colAccount'),
          getValue: (m: PaymentMethod) => m.accountNumber,
        },
      ]}
      emptyLabel={t('employeeDetail.payment.noPayment')}
      tabData={tabData}
      stretch={stretch}
      collapsible={collapsible}
      defaultExpanded={defaultExpanded}
    />
  );
}
